import StickyCartProductQuantityOrdered from "@NA/Components/StickyCartProductQuantityOrdered";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProduct,
    withProduct,
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainer, GridItem, Typography } from "@insite/mobius/index";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { ResolveThunks, connect, useSelector } from "react-redux";
import { css } from "styled-components";
import { isPriceAvailable } from "@NA/Utils/Utility";
import { getProp } from "@insite/mobius/utilities";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";

type Props = WidgetProps &
    HasProduct &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState, ownProps: HasProduct) => {
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId && cartId != undefined ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        configurationSelection: state.pages.productDetails.configurationSelection,
        subscription: state.pages.productDetails.subscription,
        isAuthenticated: state.context.session!.isAuthenticated,
        showADIAddToCart: (adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess,
        cartState,
    };
};

const mapDispatchToProps = {
    toggleLoginDrawer,
};

export interface ADIStickyCartRightSectionStyles {
    stickyProductQuantity?: GridItemProps;
    stickyProductAddToCart?: GridItemProps;
    button?: ButtonPresentationProps;
    beforeLoginRightSection?: InjectableCss;
    loginToSeePriceButton?: ButtonPresentationProps;
    containerRightSection?: GridItemProps;
    priceMessageContainer?: InjectableCss;
}

export const adiStickyCartRightSectionStyles: ADIStickyCartRightSectionStyles = {
    button: {
        css: css`
            width: 100%;
        `,
    },
    containerRightSection: {
        css: css`
            > ${GridOffset} {
                justify-content: flex-end;
            }
        `,
    },
    stickyProductQuantity: {
        width: [0, 0, 0, 3, 3],
        css: css`
            padding-left: 0;
            padding-right: 10px;
        `,
    },
    stickyProductAddToCart: {
        width: [12, 12, 9, 9, 8],
        css: css`
            padding-left: 0px;
            padding-right: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-right: 0px;
                    `,
                    css`
                        padding-right: 0px;
                    `,
                    css``,
                    css``,
                    null,
                ])}
            > button {
                border-radius: 4px;
                min-height: 40px;
                width: 147px !important;
                > img {
                    width: 35px;
                }
            }
        `,
    },
    beforeLoginRightSection: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: end;
            > ${TypographyStyle} {
                font-size: 14px;
                margin-bottom: 10px;
                margin-top: 4px;
                color: #000000; // currently adding hardcoded colors as style guide is missing
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none;
                            `,
                            css`
                                width: 100%;
                                text-align: end;
                                font-size: 12px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    loginToSeePriceButton: {
        variant: "primary",
        css: css`
            width: 225px;
            border-radius: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 160px;
                    `,
                    css`
                        width: 160px;
                    `,
                    null,
                ])}
        `,
    },
    priceMessageContainer: {
        css: css`
            display: flex;
            align-items: flex-start;
            max-height: initial !important;
            position: static;
            margin-top: 10px;
            font-weight: bold !important;
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            span {
                font-weight: bold !important;
                font-size: ${getProp("theme.typography.headerSecondary.size")};
            }
        `,
    },
};

const styles = adiStickyCartRightSectionStyles;

const ADIStickyCartRightSection = ({
    productContext,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    isAuthenticated,
    toggleLoginDrawer,
    showADIAddToCart,
    cartState,
}: Props) => {
    const onLogintoSeePriceClick = () => {
        toggleLoginDrawer({ loginDrawer: true });
    };

    const pricingIsAvailable =
        cartState?.value?.type === "Quote"
            ? true
            : isPriceAvailable(productContext.productInfo.pricing, productContext.productInfo.qtyOrdered);
    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );

    return (
        <StyledWrapper>
            {!isAuthenticated ? (
                <StyledWrapper {...styles.beforeLoginRightSection}>
                    <Typography style={{ whiteSpace: "nowrap" }} as="p">
                        {translate("Please sign in to view pricing and availability and to add to cart.")}
                    </Typography>
                    <Button {...styles.loginToSeePriceButton} onClick={onLogintoSeePriceClick}>
                        {translate("Sign In")}
                    </Button>
                </StyledWrapper>
            ) : pricingIsAvailable ? (
                <GridContainer {...styles.containerRightSection}>
                    {showADIAddToCart && (
                        <>
                            <GridItem {...styles.stickyProductQuantity}>
                                <StickyCartProductQuantityOrdered
                                    configurationCompleted={configurationCompleted}
                                    variantSelectionCompleted={variantSelectionCompleted}
                                />
                            </GridItem>
                            <GridItem {...styles.stickyProductAddToCart}>
                                <ProductAddToCartButton
                                    configurationSelection={configurationSelection}
                                    configurationCompleted={configurationCompleted}
                                    variantSelectionCompleted={variantSelectionCompleted}
                                    subscription={subscription}
                                    extendedStyles={styles.button}
                                    data-test-selector="addProductToCart"
                                />
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            ) : (
                <StyledWrapper {...styles.priceMessageContainer}>
                    <StyledWrapper>
                        {!isPendingVerification ? (
                            <Typography>{translate("Pricing Currently Not Available")}</Typography>
                        ) : (
                            <Typography>{translate("Account Pending Verification")}</Typography>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(withProductContext(ADIStickyCartRightSection))),
    definition: {
        group: "Common",
        displayName: "ADI Sticky Cart Right Section",
    },
};

export default widgetModule;
